import { type MultiSelectDropdownItem } from '@precis-digital/kurama/MultiSelectDropdown'
import { createColorMap, type BadgeColor } from '@precis-digital/kurama/Badge'
import { type Theme } from '@precis-digital/kurama/theme'
import {
  formatDateToString,
  getCorrespondingPreviousPeriodDate,
  getCorrespondingPreviousYearDate,
  getDifferenceInDays
} from 'shared/dateFns'
import { LOCAL_STORAGE_KEYS, store as saveToStore } from 'shared/lstore'
import { capitalize, isNilOrEmpty } from 'shared/utils'
import { type ProgressOverTimeRecords } from './ProgressOvertime'
import { type ChartLegendProps } from 'shared/components/ChartLegend'
import { type TFunction } from 'i18next'
import { type ActiveFilter } from 'shared/components/Filter'
import { type Filter } from 'shared/api/analytics'
import { transformSingleActiveFilterToCirrusAPIFormat } from 'shared/components/Filter/transform'
import { type Metric } from 'src/home/reportTemplateParameters'
import { type StandardReportResp, type ReportTemplateId } from 'shared/api/standardReports'
import { head } from 'ramda'
import { type ConfigStatus } from 'shared/configStatuses'
import {
  CHANNEL,
  CONVERSION_VALUE,
  type DIMENSIONS,
  MARKET,
  REPORT_TEMPLATE_WITH_CONVERSION_EVENTS,
  ROAS,
  SPEND,
  STATUSES_TO_SHOW_HOMEPAGE_DATA_FOR
} from 'src/home/constants'

const PREVIOUS = 'previous'

export type TimePeriodType = 'previous_year' | 'previous_period' | 'custom'

export interface CustomComparisonRange {
  startDate: string
  endDate: string
}

/**
 * Parses a persisted custom comparison range from local storage. Returns null when the
 * stored value is missing or malformed, so callers can safely fall back to a derived range.
 */
export const parseStoredCustomComparisonRange = (storedValue: string | null): CustomComparisonRange | null => {
  if (storedValue == null) {
    return null
  }
  try {
    const parsed = JSON.parse(storedValue) as Partial<CustomComparisonRange>
    if (typeof parsed?.startDate === 'string' && typeof parsed?.endDate === 'string') {
      return { startDate: parsed.startDate, endDate: parsed.endDate }
    }
  } catch {
    return null
  }
  return null
}

/**
 * Persist the comparison period type on Apply. The custom range must not be written
 * here: DatePickerV2 runs onApplyDateRangeClick before the trigger commits
 * onSelectComparisonRange, so the applied window is not known yet.
 */
export const persistHomeComparisonPeriodSelection = (
  reportId: string | undefined,
  periodType: TimePeriodType
): void => {
  if (reportId == null) {
    return
  }
  saveToStore(LOCAL_STORAGE_KEYS.HOME_PAGE_COMPARISON_PERIOD_SELECTION(reportId), periodType)
}

/**
 * Persist the custom comparison window when the picker first commits it — after Apply.
 * The `custom` guard belongs here so a later Apply-order change cannot store a
 * previous-period window, and so the first custom Apply still writes once the
 * period type is already `custom`.
 */
export const persistHomeCustomComparisonRange = (
  reportId: string | undefined,
  periodType: TimePeriodType,
  range: CustomComparisonRange
): void => {
  if (reportId == null || periodType !== 'custom') {
    return
  }
  saveToStore(LOCAL_STORAGE_KEYS.HOME_PAGE_CUSTOM_COMPARISON_RANGE(reportId), JSON.stringify(range))
}

export const ACTIVE_FILTER_PARAMETER_OPTIONS = ['market_name', 'channel_grouping'] as const

/** Field variable names used by channel-grouping report templates. */
export const CHANNEL_GROUPING_FILTER_FIELD_NAMES = ['market_channel_grouping', 'custom_channel_grouping'] as const

/** All filter field names persisted in URL query params across report templates. */
export const HOME_PAGE_FILTER_FIELD_NAMES = [
  ...ACTIVE_FILTER_PARAMETER_OPTIONS,
  ...CHANNEL_GROUPING_FILTER_FIELD_NAMES
] as const

export const SCORECARD_METRICS_MINIMUM_SELECTION = 2
export const SCORECARD_METRICS_MAXIMUM_SELECTION = 12
export const CONVERSION_EVENT_RELATED_METRICS: Metric[] = ['conversionValue', 'conversions', 'costPerAction', 'roas']

export type HomePageFilterParameter = (typeof HOME_PAGE_FILTER_FIELD_NAMES)[number]
export const DETAILED_TABLE_DEFAULT_OPTIONS = [CHANNEL, MARKET, SPEND, CONVERSION_VALUE, ROAS] as const
export const DETAILED_TABLE_MIN_DIMENSIONS = 1
export const DETAILED_TABLE_MIN_METRICS = 1

export type HomePageTableDimension = (typeof DIMENSIONS)[number]

export const PROGRESS_OVER_TIME_CHART_MIN_METRICS = 1
export const PROGRESS_OVER_TIME_CHART_MAX_METRICS = 2
export const PROGRESS_OVER_TIME_CHART_MAX_BREAKDOWN = 6

export type ProgressOverTimeChartAggregation = 'daily' | 'cumulative'

export interface ProgressOverTimeBreakdownOption {
  label: string
  value: string
}

export const formDataKey = ({
  metric,
  breakdown,
  isPreviousMetric = false
}: {
  metric: Metric
  breakdown?: string | null
  isPreviousMetric?: boolean
}): string => {
  let dataKey = metric

  if (breakdown != null) {
    dataKey += `_${breakdown}`
  }

  if (isPreviousMetric) {
    dataKey += `_${PREVIOUS}`
  }

  return dataKey
}

export const getPartsFromDataKey = (dataKey: string): [Metric, string | null, boolean] => {
  const parts = dataKey.split('_')

  const metric = parts[0]

  const hasBreakdown = parts.length > 1 && parts[1] !== PREVIOUS
  const breakdown = hasBreakdown ? parts[1] : null

  const isPreviousMetric = dataKey.endsWith(PREVIOUS) ?? false

  return [metric as Metric, breakdown, isPreviousMetric]
}

type LineConfig = Record<
  string,
  {
    lineColor: string
    badgeColor: BadgeColor
    lineOpacity?: number
  }
>

const sortDataKeys = (dataKeyA: string, dataKeyB: string): number => {
  if (dataKeyA.endsWith(PREVIOUS) && !dataKeyB.endsWith(PREVIOUS)) {
    return 1
  } else if (!dataKeyA.endsWith(PREVIOUS) && dataKeyB.endsWith(PREVIOUS)) {
    return -1
  } else {
    return 0
  }
}

const chartColors: BadgeColor[] = ['info', 'dark-mint', 'dark-red', 'dark-indigo', 'steel', 'purple']

export const getProgressOverTimeLineConfigs = (progressOverTime: ProgressOverTimeRecords, theme: Theme): LineConfig => {
  const colorMap = createColorMap(theme)
  if (isNilOrEmpty(progressOverTime)) {
    return {}
  }

  const dataKeys = Object.keys(progressOverTime[0]).filter((key) => !['x', 'fullDate'].includes(key))

  const chartColorMap: Record<
    string,
    {
      lineColor: string
      badgeColor: BadgeColor
      lineOpacity?: number
    }
  > = {}

  dataKeys.sort(sortDataKeys)

  dataKeys.forEach((dataKey, index) => {
    const [metric, breakdown, isPreviousMetric] = getPartsFromDataKey(dataKey)

    if (isPreviousMetric) {
      chartColorMap[dataKey] = {
        lineColor: chartColorMap[formDataKey({ metric, breakdown })].lineColor,
        lineOpacity: 0.15,
        badgeColor: chartColorMap[formDataKey({ metric, breakdown })].badgeColor
      }
    } else {
      const badgeColor = chartColors[index % chartColors.length]
      const lineColor = colorMap.get(chartColors[index % chartColors.length])

      if (lineColor != null) {
        chartColorMap[dataKey] = {
          lineColor,
          badgeColor
        }
      }
    }
  })

  return chartColorMap
}

export const formatTooltipDescriptionByDataKey = ({
  dataKey,
  payload,
  timePeriod,
  selectedBreakdownCount,
  selectedPeriodType,
  t
}: {
  dataKey: string
  payload: Record<string, string | number>
  timePeriod: [string, string]
  selectedBreakdownCount: number
  selectedPeriodType: TimePeriodType
  t: TFunction<'home'>
}): string => {
  const [metric, currentBreakdown, isPreviousMetric] = getPartsFromDataKey(dataKey)

  const correspondingPreviousDate = formatDateToString({
    date:
      selectedPeriodType === 'previous_year'
        ? getCorrespondingPreviousYearDate(payload.fullDate?.toString())
        : getCorrespondingPreviousPeriodDate(payload.fullDate?.toString(), getDifferenceInDays(...timePeriod))
  })

  let titleKey: string = t(metric)

  if (currentBreakdown != null) {
    titleKey = `${titleKey} • ${currentBreakdown}`
  }

  if (selectedBreakdownCount <= 2) {
    titleKey = `${titleKey} • ${
      isPreviousMetric ? correspondingPreviousDate : formatDateToString({ date: payload.fullDate })
    }`
  }

  return titleKey
}

export const getChartLegendData = ({
  progressOverTime,
  lineConfigs,
  t,
  selectedMetricsCount,
  selectedBreakdownCount,
  selectedPeriodType
}: {
  progressOverTime: ProgressOverTimeRecords
  lineConfigs: LineConfig
  t: TFunction<'home'>
  selectedMetricsCount: number
  selectedBreakdownCount: number
  selectedPeriodType: TimePeriodType
}): ChartLegendProps['selectedRecords'] => {
  if (isNilOrEmpty(progressOverTime)) {
    return {}
  }

  const dataKeys = Object.keys(progressOverTime[0]).filter((key) => !['x', 'fullDate'].includes(key))

  const legendRecords: ChartLegendProps['selectedRecords'] = {}

  dataKeys.sort(sortDataKeys)
  dataKeys.forEach((key) => {
    const [metric, breakdown, isPreviousMetric] = getPartsFromDataKey(key)
    let legendText = ''

    const previousPeriodText =
      selectedPeriodType === 'previous_year'
        ? t('progressOverTime.previousYear')
        : t('progressOverTime.previousTimePeriod')

    const suffix = isPreviousMetric ? ` • ${previousPeriodText}` : ''
    if (selectedBreakdownCount === 0) {
      legendText = `${t(metric)}${suffix}`
    } else {
      legendText = `${t(metric)} • ${capitalize(breakdown ?? '')}${suffix}`
    }

    legendRecords[legendText] = {
      badgeColor: lineConfigs[key].badgeColor,
      opacity: lineConfigs[key].lineOpacity
    }
  })

  return legendRecords
}

export const transformFiltersToAPIFormat = ({
  selectedMarketAndChannelFilters,
  selectedConversionEvents
}: {
  selectedMarketAndChannelFilters: ActiveFilter[]
  selectedConversionEvents?: MultiSelectDropdownItem[]
}): Filter[] => {
  const filters = selectedMarketAndChannelFilters.map(transformSingleActiveFilterToCirrusAPIFormat)

  if (selectedConversionEvents != null && selectedConversionEvents.length > 0) {
    filters.push({
      field: 'source_conv_name',
      operator: 'IN',
      value: selectedConversionEvents.map((event) => {
        const prefix = event.subCategory != null ? `${event.subCategory}: ` : ''
        return `${prefix}${event.value}`
      })
    })
  }

  return filters
}

const PRIORITY_REPORT_TEMPLATES: ReportTemplateId[] = ['reporting_base']

const sortReportByRunSchedule = (firstRunSchedule: boolean, secondRunSchedule: boolean): number => {
  if (firstRunSchedule && !secondRunSchedule) {
    return -1
  }
  if (!firstRunSchedule && secondRunSchedule) {
    return 1
  }
  return 0
}

const sortReportByTemplateId = (firstTemplateId: ReportTemplateId, secondTemplateId: ReportTemplateId): number => {
  if (PRIORITY_REPORT_TEMPLATES.includes(firstTemplateId) && !PRIORITY_REPORT_TEMPLATES.includes(secondTemplateId)) {
    return -1
  }
  if (!PRIORITY_REPORT_TEMPLATES.includes(firstTemplateId) && PRIORITY_REPORT_TEMPLATES.includes(secondTemplateId)) {
    return 1
  }

  return firstTemplateId >= secondTemplateId ? 1 : -1
}

export const sortReportsByParameters = <
  T extends {
    templateId?: ReportTemplateId
    name: string
    runSchedule?: boolean
  }
>(
  a: T,
  b: T
): number => {
  if (a.runSchedule != null && b.runSchedule != null && a.runSchedule !== b.runSchedule) {
    return sortReportByRunSchedule(a.runSchedule, b.runSchedule)
  }

  if (a.templateId != null && b.templateId != null && a.templateId !== b.templateId) {
    return sortReportByTemplateId(a.templateId, b.templateId)
  }

  return a.name >= b.name ? 1 : -1
}

export interface AllowedStandardReport extends StandardReportResp {
  templateId: 'reporting_base' | 'channel_grouping_performance_report_v2'
}

export const getSelectedStandardReportConfig = (
  standardReportsConfig: AllowedStandardReport[],
  selectedReportId?: string | null
): AllowedStandardReport | undefined => {
  let selectedReport: AllowedStandardReport | undefined
  if (selectedReportId != null) {
    selectedReport = standardReportsConfig.find((report) => report.configId === parseInt(selectedReportId))
  }

  if (selectedReport == null) {
    standardReportsConfig.sort(sortReportsByParameters)
    selectedReport = head(standardReportsConfig)
  }

  return selectedReport
}

export const checkIfReportConfigAndStatusLoaded = ({
  hasSuccessfullyLoadedStandardReportsConfig,
  isLoadingReportStatuses,
  getStandardReportStatusesRequestIsIdle
}: {
  hasSuccessfullyLoadedStandardReportsConfig: boolean
  isLoadingReportStatuses: boolean
  getStandardReportStatusesRequestIsIdle: boolean
}): boolean =>
  hasSuccessfullyLoadedStandardReportsConfig && !isLoadingReportStatuses && !getStandardReportStatusesRequestIsIdle

export const checkIfCustomContentConditionSatisfied = ({
  isReportConfigAndStatusLoaded,
  shouldShowErrorView,
  shouldShowCustomNonErrorView,
  shouldShowNoDataView
}: {
  isReportConfigAndStatusLoaded: boolean
  shouldShowErrorView: boolean
  shouldShowCustomNonErrorView: boolean
  shouldShowNoDataView: boolean
}): boolean =>
  isReportConfigAndStatusLoaded && (shouldShowErrorView || shouldShowCustomNonErrorView || shouldShowNoDataView)

export const checkIfToolbarConditionSatisfied = ({
  isCustomContentConditionSatisfied,
  isDataLoaded,
  isFilterOptionsLoaded,
  isConversionNameAndSourceConditionSatisfied
}: {
  isCustomContentConditionSatisfied: boolean
  isDataLoaded: boolean
  isFilterOptionsLoaded: boolean
  isConversionNameAndSourceConditionSatisfied: boolean
}): boolean =>
  isCustomContentConditionSatisfied ||
  (isDataLoaded && isFilterOptionsLoaded && isConversionNameAndSourceConditionSatisfied)

export const checkIfReportTemplateWithNoConversionEvents = (
  selectedStandardReportConfig: StandardReportResp | undefined
): boolean =>
  selectedStandardReportConfig != null &&
  !REPORT_TEMPLATE_WITH_CONVERSION_EVENTS.includes(selectedStandardReportConfig.templateId)

export const checkIfValidStatusForShowingHomePageData = (selectedReportStatus: ConfigStatus | undefined): boolean =>
  selectedReportStatus != null && STATUSES_TO_SHOW_HOMEPAGE_DATA_FOR.includes(selectedReportStatus)

export const checkIfFilterConditionIsSatisfied = (
  isValidStatusForShowingHomePageData: boolean,
  filterDataCount = 0
): boolean => isValidStatusForShowingHomePageData && filterDataCount > 0

export const checkIfNoReportDataViewConditionSatisfied = ({
  hasSuccessfullyLoadedNecessaryData,
  isDataError,
  dataAnalyticsCount,
  status
}: {
  hasSuccessfullyLoadedNecessaryData: boolean
  isDataError: boolean
  dataAnalyticsCount: number
  status: ConfigStatus | undefined
}): boolean => hasSuccessfullyLoadedNecessaryData && !isDataError && status !== 'error' && dataAnalyticsCount === 0

export const fillMissingLineChartData = ({
  allDates,
  lineChartData
}: {
  allDates: string[]
  lineChartData: ProgressOverTimeRecords
}): ProgressOverTimeRecords => {
  const existingDates = new Map(
    lineChartData.map((item) => [formatDateToString({ date: item.fullDate, targetFormat: 'yyyy-MM-dd' }), item])
  )

  const progressOverTime = allDates.map((dateStr) => {
    const data = existingDates.get(dateStr)
    if (data == null) {
      return {
        x: formatDateToString({ date: dateStr, targetFormat: 'MMM do' }),
        fullDate: new Date(dateStr).toUTCString()
      }
    }
    return data
  })

  return progressOverTime
}

export interface ConversionEventsDefault {
  source: string
  name: string
}

/**
 * Selects a single default conversion event from a list of options based on a prioritized list of defaults.
 * It looks for the "primary" default first. If it's not available, it then looks for the "secondary", and so on.
 * If no prioritized defaults are found in the options, it falls back to the first available option.
 * @param options - The list of available conversion event options.
 * @param conversionEventsDefaults - A prioritized list of default conversion events to look for.
 * @returns An array containing the single selected default conversion event, or an empty array if no suitable event is found.
 */
export const getDefaultConversionEvents = (
  options: MultiSelectDropdownItem[] | undefined,
  conversionEventsDefaults: ConversionEventsDefault[]
): MultiSelectDropdownItem[] => {
  let defaultConversionEvent: MultiSelectDropdownItem | undefined
  for (const eventDefault of conversionEventsDefaults) {
    defaultConversionEvent = options?.find(
      (option) => option.value === eventDefault.name && option.subCategory === eventDefault.source
    )
    if (defaultConversionEvent != null) {
      break
    }
  }

  defaultConversionEvent ??= options?.[0]

  return defaultConversionEvent != null ? [defaultConversionEvent] : []
}
